import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import { FileSpreadsheet, Download, Upload, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const downloadTemplate = async () => {
  try {
    const res = await api.get("/import/template", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "Template_Import_Trasporti.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("Impossibile scaricare il template"); }
};

const ResultTable = ({ result, navigate }) => (
  <div className="grid md:grid-cols-2 gap-4 mt-6">
    <Card className="p-5 border-emerald-200 bg-emerald-50/40">
      <p className="nx-label text-emerald-800">Importati</p>
      <p data-testid="import-success-count" className="text-3xl font-bold font-heading text-emerald-700">{result.imported_count}</p>
      {result.imported.length > 0 && (
        <ul className="mt-3 space-y-1 max-h-72 overflow-auto text-xs">
          {result.imported.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-2 border-b border-emerald-100 py-1">
              <button type="button" className="font-mono font-semibold text-primary hover:underline" onClick={() => navigate(`/operativa/richiesta/${r.id}`)}>{r.code}</button>
              <span className="truncate text-slate-700">{r.paziente}</span>
              <span className="text-slate-500 shrink-0">{formatDateTime(r.data_ora)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
    <Card className={`p-5 ${result.error_count ? "border-red-200 bg-red-50/40" : "border-slate-200"}`}>
      <p className={`nx-label ${result.error_count ? "text-red-800" : ""}`}>Righe con errori</p>
      <p data-testid="import-error-count" className={`text-3xl font-bold font-heading ${result.error_count ? "text-red-700" : "text-slate-400"}`}>{result.error_count}</p>
      {result.errors.length > 0 && (
        <Table className="mt-3">
          <TableHeader><TableRow><TableHead className="w-16">Riga</TableHead><TableHead>Paziente</TableHead><TableHead>Errore</TableHead></TableRow></TableHeader>
          <TableBody>
            {result.errors.map((e) => (
              <TableRow key={e.riga} data-testid={`import-error-row-${e.riga}`}>
                <TableCell className="font-mono text-xs">{e.riga}</TableCell>
                <TableCell className="text-xs">{e.paziente || "—"}</TableCell>
                <TableCell className="text-xs text-red-700">{e.errore}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  </div>
);

export default function ImportPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) { toast.error("Formato non valido", { description: "Carica un file Excel .xlsx" }); return; }
    setFile(f); setResult(null);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.imported_count) toast.success(`${data.imported_count} richieste importate`, { description: data.error_count ? `${data.error_count} righe con errori` : "Nessun errore" });
      else toast.warning("Nessuna richiesta importata", { description: "Controlla gli errori riportati" });
    } catch (err) {
      toast.error("Importazione fallita", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2">
          <FileSpreadsheet className="h-7 w-7 text-primary" /> Importazione Massiva Trasporti
        </h1>
        <p className="text-sm text-slate-500 mb-6">Carica un file Excel per creare più richieste di trasporto in un solo passaggio. Le richieste vengono pubblicate a tutti i partner (FCFS).</p>

        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-5 border-slate-200">
            <div className="flex items-center gap-2 mb-2"><span className="h-6 w-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">1</span><p className="font-semibold text-slate-800 text-sm">Scarica il template</p></div>
            <p className="text-xs text-slate-500 mb-4">Contiene i nomi corretti delle colonne, una riga di esempio e le istruzioni. Le colonne con * sono obbligatorie.</p>
            <Button data-testid="download-template-button" variant="outline" className="gap-2 w-full" onClick={downloadTemplate}><Download className="h-4 w-4" /> Template Excel</Button>
          </Card>
          <Card className="p-5 border-slate-200 md:col-span-2">
            <div className="flex items-center gap-2 mb-2"><span className="h-6 w-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">2</span><p className="font-semibold text-slate-800 text-sm">Carica il file compilato</p></div>
            <div
              data-testid="import-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              onClick={() => inputRef.current?.click()}
              className="mt-2 border-2 border-dashed border-slate-300 hover:border-primary rounded-lg p-6 text-center cursor-pointer transition-colors"
            >
              <Upload className="h-6 w-6 mx-auto text-slate-400 mb-2" />
              {file ? (
                <p data-testid="import-file-name" className="text-sm font-medium text-slate-800">{file.name} <span className="text-slate-400">({Math.round(file.size / 1024)} KB)</span></p>
              ) : (
                <p className="text-sm text-slate-500">Trascina qui il file .xlsx o clicca per selezionarlo</p>
              )}
              <input ref={inputRef} data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            </div>
            <div className="flex justify-end mt-4">
              <Button data-testid="import-submit-button" disabled={!file || busy} onClick={upload} className="gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importa richieste
              </Button>
            </div>
          </Card>
        </div>

        {result && (
          <div data-testid="import-result" className="mt-8">
            <div className="flex items-center gap-2">
              {result.error_count ? <AlertTriangle className="h-5 w-5 text-amber-500" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
              <h2 className="text-lg font-semibold text-slate-800">Riepilogo importazione</h2>
            </div>
            <ResultTable result={result} navigate={navigate} />
            {result.imported_count > 0 && (
              <div className="flex justify-end mt-4">
                <Button data-testid="go-dashboard-button" variant="outline" onClick={() => navigate("/operativa")}>Vai alla dashboard</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
